<template>
    <div class="face-recognition">
        <div class="camera-container">
            <div class="preview-area">
                <img v-if="previewImage" :src="previewImage" class="preview-image" />
                <div v-else class="empty-preview">
                    <div class="face-frame">
                        <div class="corner top-left"></div>
                        <div class="corner top-right"></div>
                        <div class="corner bottom-left"></div>
                        <div class="corner bottom-right"></div>
                    </div>
                    <div class="tips-text">{{ tipsText }}</div>
                </div>
            </div>
        </div>

        <div class="button-area">
            <input type="file" ref="albumInput" accept="image/*" capture="user" style="display: none"
                @change="handleFileChange" />
            <van-button v-if="!previewImage" type="primary" size="large" @click="selectFromAlbum">
                开始拍照
            </van-button>
            <template v-else>
                <van-button type="default" size="large" @click="retakePhoto">重新拍照</van-button>
                <van-button type="primary" size="large" @click="uploadPhoto" :loading="isUploading">
                    {{ isUploading ? '上传中...' : '确认上传' }}
                </van-button>
            </template>
        </div>


    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { Dialog } from 'vant'

// 响应式数据
const isUploading = ref(false)
const errorMessage = ref('')
const tipsText = ref('请将脸部置于框内拍照')
const previewImage = ref(null)
const partnerId = ref(false)
const albumInput = ref(null)
const model = ref('')

onMounted(() => {
    setWxTitle('人脸录入')
    const urlParams = new URLSearchParams(window.location.search)
    partnerId.value = urlParams.get('partner_id') || false
    model.value = urlParams.get('model') || ''
})

function goBack() {
    window.history.back()
}

// 从相册选择
function selectFromAlbum() {
    albumInput.value.click()
}

// 处理文件选择
async function handleFileChange(event) {
    const file = event.target.files[0]
    if (!file) return

    // 检查文件类型
    if (!file.type.startsWith('image/')) {
        Dialog.alert({
            title: '提示',
            message: '请选择图片文件'
        })
        return
    }

    // 检查文件大小（限制为10MB）
    if (file.size > 10 * 1024 * 1024) {
        Dialog.alert({
            title: '提示',
            message: '图片大小不能超过10MB'
        })
        return
    }

    // 预览图片
    previewImage.value = URL.createObjectURL(file)
}

// 重新拍照
function retakePhoto() {
    if (previewImage.value) {
        URL.revokeObjectURL(previewImage.value)
    }
    previewImage.value = null
    albumInput.value.value = ''
}

// 上传照片
async function uploadPhoto() {
    if (!previewImage.value) return

    isUploading.value = true
    try {
        // 将预览图片转换为Blob
        const response = await fetch(previewImage.value)
        const blob = await response.blob()

        // 转换为base64
        const reader = new FileReader()
        const base64Image = await new Promise((resolve) => {
            reader.onloadend = async () => {
                const base64 = reader.result.split(',')[1]
                // 压缩图片
                const compressedBase64 = await compressImage(base64)
                resolve(compressedBase64)
            }
            reader.readAsDataURL(blob)
        })

        const formData = new FormData()
        formData.append('image', base64Image)

        // 上传人脸数据
        const res = await post('/baidu/check/face', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })

        if (res.code === 0) {
            // 获取人脸特征
            const res1 = await post('/baidu/face/feature', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            if (res1.code === 0) {
                // 普通模式：调用添加人脸接口
                const params = {
                    baidu_id: res1.data.feature,
                    partner_id: partnerId.value ? parseInt(partnerId.value) : parseInt(localStorage.getItem('userId')),
                    image: base64Image
                }
                if (model.value === 'face_collection') {
                    params.collection_id = parseInt(localStorage.getItem('userId'))
                }
                const res2 = await post('/api/add/face', params)

                if (res2.result && res2.result.state === 'ok') {
                    Dialog.alert({
                        title: '录入成功',
                        message: '人脸信息录入成功！',
                        confirmButtonText: '完成'
                    }).then(() => {
                        goBack()
                    })
                } else {
                    Dialog.alert({
                        title: '提示',
                        message: '添加人脸失败：' + (res2.result?.message || res2.msg || '未知错误')
                    })
                }
            } else {
                Dialog.alert({
                    title: '提示',
                    message: '获取人脸特征失败：' + (res1.msg || '未知错误')
                })
            }
        } else {
            console.error('人脸检测失败：' + (res.msg || '未知错误'))
            Dialog.alert({
                title: '提示',
                message: '人脸检测失败：' + (res.msg || '未知错误')
            })
        }
    } catch (error) {
        console.error('上传失败:', error)
        Dialog.alert({
            title: '提示',
            message: '上传失败，请重试'
        })
    } finally {
        isUploading.value = false
    }
}

// 添加图片压缩方法
function compressImage(base64, quality = 0.6) {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const img = new Image()

        img.onload = () => {
            // 计算压缩后的尺寸，最大宽度设为 1280px
            let width = img.width
            let height = img.height
            const maxWidth = 1280

            if (width > maxWidth) {
                height = (maxWidth * height) / width
                width = maxWidth
            }

            canvas.width = width
            canvas.height = height

            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)

            // 输出压缩后的base64
            const compressedBase64 = canvas.toDataURL('image/jpeg', quality).split(',')[1]
            resolve(compressedBase64)
        }

        img.src = `data:image/jpeg;base64,${base64}`
    })
}
</script>

<style scoped>
.face-recognition {
    min-height: 100vh;
    background: #fff;
    display: flex;
    flex-direction: column;
}

.camera-container {
    flex: 1;
    position: relative;
}

.preview-area {
    width: 100%;
    height: calc(100vh - 84px);
    position: relative;
    overflow: hidden;
    border-radius: 10px;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.empty-preview {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.face-frame {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    height: 200px;
    border-radius: 10px;
}

.corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid #fff;
}

.corner.top-left {
    top: 0;
    left: 0;
    border-right: none;
    border-bottom: none;
    border-top-left-radius: 10px;
}

.corner.top-right {
    top: 0;
    right: 0;
    border-left: none;
    border-bottom: none;
    border-top-right-radius: 10px;
}

.corner.bottom-left {
    bottom: 0;
    left: 0;
    border-right: none;
    border-top: none;
    border-bottom-left-radius: 10px;
}

.corner.bottom-right {
    bottom: 0;
    right: 0;
    border-left: none;
    border-top: none;
    border-bottom-right-radius: 10px;
}

.tips-text {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    color: #fff;
    font-size: 14px;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.button-area {
    padding: 20px;
    background: #fff;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    gap: 12px;
}

.button-area .van-button {
    flex: 1;
    height: 44px;
    border-radius: 22px;
    font-size: 16px;
}

.button-area .van-button--primary {
    background: var(--van-primary-color);
    border-color: var(--van-primary-color);
}
</style>